import { Trash2 } from 'lucide-react';
import { formatMinutes } from '../../../lib/calendar/team/availability';
import { WEEK_ORDER, WEEKDAY_LABELS, WEEKDAY_SHORT, type AvailabilityBlock, type WeekHours } from '../../../lib/calendar/team/workHours';
import { ChoiceSelect, LinkAction } from './fields';

/** Orari a passi di 15 minuti, sempre a 24 ore (il campo ora del browser segue la lingua del sistema e puo' mostrare 6:00 PM). */
const TIMES = Array.from({ length: 24 * 4 + 1 }, (_, i) => i * 15).map(m => ({ value: String(m), label: formatMinutes(m) }));

/** Ora inline: elenco a passi di 15 minuti. */
export function TimeField({ value, onChange, label }: { value: number; onChange: (m: number) => void; label: string }) {
  const choices = TIMES.some(t => t.value === String(value)) ? TIMES : [...TIMES, { value: String(value), label: formatMinutes(value) }].sort((a, b) => +a.value - +b.value);
  return <ChoiceSelect label={label} value={String(value)} choices={choices} onChange={v => onChange(Number(v))} />;
}

/** Settimana di lavoro: una riga per giorno (da lunedi), spunta "lavora" e orario. */
export function WeekHoursEditor({ week, onChange }: { week: WeekHours; onChange: (w: WeekHours) => void }) {
  const set = (d: number, v: WeekHours[number]) => onChange(week.map((h, i) => (i === d ? v : h)));
  return (
    <div>
      {WEEK_ORDER.map(d => {
        const h = week[d];
        return (
          <div key={d} className="flex flex-wrap items-center gap-x-3 min-h-[34px] border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
            <label className="flex items-center gap-2 w-32 text-[13.5px] text-[#37352F] dark:text-white cursor-pointer">
              <input type="checkbox" checked={!!h} className="accent-[#37352F]"
                onChange={e => set(d, e.target.checked ? { start: 9 * 60, end: 18 * 60 } : null)} />
              {WEEKDAY_LABELS[d]}
            </label>
            {h ? (
              <span className="flex items-center gap-1 text-[13px] text-[#787774]">
                dalle <TimeField label={`${WEEKDAY_LABELS[d]} dalle`} value={h.start} onChange={m => set(d, { ...h, start: m })} />
                alle <TimeField label={`${WEEKDAY_LABELS[d]} alle`} value={h.end} onChange={m => set(d, { ...h, end: m })} />
                {h.end <= h.start && <span className="text-red-600">la fine viene prima dell'inizio</span>}
              </span>
            ) : <span className="text-[13px] text-[#9B9A97]">non lavora</span>}
          </div>
        );
      })}
    </div>
  );
}

/** Pause e blocchi ricorrenti: nome, giorni, orario. */
export function BlocksEditor({ blocks, onChange }: { blocks: AvailabilityBlock[]; onChange: (b: AvailabilityBlock[]) => void }) {
  const patch = (i: number, p: Partial<AvailabilityBlock>) => onChange(blocks.map((b, k) => (k === i ? { ...b, ...p } : b)));
  const add = () => onChange([...blocks, { id: `blocco_${Date.now().toString(36)}`, title: 'Pausa pranzo', days: [1, 2, 3, 4, 5], start: 13 * 60, end: 14 * 60 }]);
  return (
    <div>
      {blocks.length === 0 && <div className="py-2 text-[13px] text-[#9B9A97]">Nessuna pausa.</div>}
      {blocks.map((b, i) => (
        <div key={b.id} className="group flex flex-wrap items-center gap-x-3 gap-y-1 py-1 min-h-[36px] border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <input aria-label="Nome della pausa" value={b.title} placeholder="Nome (es. Pausa pranzo)" onChange={e => patch(i, { title: e.target.value })}
            className="w-40 min-w-0 rounded px-1.5 py-0.5 text-[13.5px] bg-transparent border border-transparent hover:border-[rgba(55,53,47,0.16)] focus:border-[rgba(55,53,47,0.3)] focus:outline-none text-[#37352F] dark:text-white" />
          <span className="flex items-center gap-0.5">
            {WEEK_ORDER.map(d => {
              const on = b.days.includes(d);
              return (
                <button key={d} type="button" aria-pressed={on} aria-label={WEEKDAY_LABELS[d]} title={WEEKDAY_LABELS[d]}
                  onClick={() => patch(i, { days: on ? b.days.filter(x => x !== d) : [...b.days, d] })}
                  className={`w-6 h-6 rounded text-[11.5px] ${on ? 'bg-[#37352F] text-white dark:bg-white dark:text-[#191919]' : 'text-[#9B9A97] hover:bg-[rgba(55,53,47,0.08)]'}`}>
                  {WEEKDAY_SHORT[d]}
                </button>
              );
            })}
          </span>
          <span className="flex items-center gap-1 text-[13px] text-[#787774]">
            dalle <TimeField label="Pausa dalle" value={b.start} onChange={m => patch(i, { start: m })} />
            alle <TimeField label="Pausa alle" value={b.end} onChange={m => patch(i, { end: m })} />
          </span>
          {(b.end <= b.start || !b.days.length) && <span className="text-[12px] text-red-600">{!b.days.length ? 'scegli almeno un giorno' : 'la fine viene prima dell\'inizio'}</span>}
          <button type="button" aria-label="Elimina pausa" onClick={() => onChange(blocks.filter((_, k) => k !== i))}
            className="ml-auto p-1 rounded text-[#9B9A97] hover:text-red-600 opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 focus:opacity-100">
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      ))}
      <div className="mt-1"><LinkAction onClick={add}>Pausa</LinkAction></div>
    </div>
  );
}
